import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { API } from '../../../../api/URL_API';
import { Link } from 'react-router-dom';
import { ArrowUp, ChevronDown } from 'react-bootstrap-icons';
import '../../styles/CategoriasUni.css';
import { createSlug } from '../../../../utils/slug';

function CategoriasUni({ idSucursal, soloSeccion = null }) {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const navRef = useRef(null);
    const [seccionActual, setSeccionActual] = useState(null);
    const esVistaSeccion = seccionActual !== null;

    // ✅ Función para determinar el estado inicial del acordeón
    const getInitialExpandedState = () => {
        const lastInteraction = localStorage.getItem("lastCategoryInteraction");
        const expandedSection = localStorage.getItem("expandedSection");

        if (!lastInteraction) {
            return 'panel0';
        }

        const timeSinceLastInteraction = Date.now() - parseInt(lastInteraction, 10);
        const thirtyMinutes = 30 * 60 * 1000;

        if (timeSinceLastInteraction > thirtyMinutes) {
            localStorage.removeItem("expandedSection");
            localStorage.removeItem("scrollPosition");
            localStorage.removeItem("lastCategoryInteraction");
            return 'panel0';
        }

        return expandedSection || 'panel0';
    };

    const [expanded, setExpanded] = useState(getInitialExpandedState());

    // ✅ Función para detectar si una sección tiene categorías nuevas
    const sectionHasNewCategories = (seccion) => {
        if (!seccion.categorias || seccion.categorias.length === 0) {
            return false;
        }

        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

        return seccion.categorias.some(categoria => {
            try {
                const lastUpdated = new Date(categoria.lastUpdated);
                return lastUpdated >= threeDaysAgo;
            } catch (error) {
                console.log("Error procesando fecha de categoría:", categoria.nomCategoria, error);
                return false;
            }
        });
    };

    // ✅ Escuchar el scroll para mostrar/ocultar el botón flotante
    useEffect(() => {
        const handleScroll = () => {
            // Mostrar el botón si se ha hecho scroll más de 300px
            if (window.scrollY > 300) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // ✅ Función para subir hasta lo más alto
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // ✅ Restaurar el scroll después de que se cargue el contenido
    useEffect(() => {
        const savedScrollPosition = localStorage.getItem("scrollPosition");

        const cargarCategorias = async () => {

            try {

                // Vista normal
                if (!soloSeccion) {

                    const response = await fetch(
                        `${API}/api/mostrarCategorias/categorias`
                    );

                    if (!response.ok) {
                        throw new Error("Error al cargar categorías");
                    }

                    const data = await response.json();

                    setData(data);

                }

                // Vista de una sección
                else {

                    // 1️⃣ Resolver slug
                    const slugResponse = await fetch(
                        `${API}/api/mostrarCategorias/seccion/slug/${soloSeccion}`
                    );

                    if (!slugResponse.ok) {
                        throw new Error("Sección no encontrada");
                    }

                    const seccion = await slugResponse.json();

                    // 2️⃣ Obtener categorías de esa sección
                    const categoriasResponse = await fetch(
                        `${API}/api/mostrarCategorias/categorias/seccion/${seccion.idSeccion}`
                    );

                    if (!categoriasResponse.ok) {
                        throw new Error("Error al cargar categorías");
                    }

                    const categorias = await categoriasResponse.json();

                    setSeccionActual(categorias);

                }

                setIsLoading(false);

                setTimeout(() => {

                    if (savedScrollPosition) {

                        window.scrollTo(
                            0,
                            parseInt(savedScrollPosition, 10)
                        );

                    }

                }, 150);

            }

            catch (error) {

                console.error(error);

                setIsLoading(false);

            }

        };

        cargarCategorias();

    }, [soloSeccion]);

    // ✅ Manejar la expansión/colapso del acordeón
    const handleAccordionChange = (panelIndex) => {
        const currentTime = Date.now();

        if (expanded === panelIndex) {
            setExpanded(false);
            localStorage.removeItem("expandedSection");
            localStorage.removeItem("scrollPosition");
            localStorage.removeItem("lastCategoryInteraction");
        } else {
            localStorage.setItem("scrollPosition", window.scrollY);
            localStorage.setItem("expandedSection", panelIndex);
            localStorage.setItem("lastCategoryInteraction", currentTime.toString());
            setExpanded(panelIndex);

            // 👇 UX improvement
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const seccionesMostrar = soloSeccion
        ? (seccionActual ? [seccionActual] : [])
        : data;

    // ✅ Guardar la posición del scroll antes de navegar a una categoría
    const handleCategoryClick = (panelIndex) => {
        const currentTime = Date.now();
        localStorage.setItem("scrollPosition", window.scrollY);
        localStorage.setItem("expandedSection", panelIndex);
        localStorage.setItem("lastCategoryInteraction", currentTime.toString());
    };

    // ✅ Componente Skeleton para la carga
    const SkeletonCard = () => (
        <div
            className="position-relative border-0 h-100 shadow-sm"
            style={{
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: '#f0f0f0'
            }}
        >
            <div
                style={{
                    width: '100%',
                    aspectRatio: '1',
                    backgroundColor: '#e9ecef',
                    animation: 'pulse 2s infinite'
                }}
            />
        </div>
    );

    return (
        <div className="py-3 py-md-4">
            <Container fluid className="px-2 px-md-4">
                {/* 📱 NAVEGACIÓN MÓVIL - STICKY */}
                {!esVistaSeccion && (
                    <div
                        ref={navRef}
                        className="d-md-none nav-sticky-mobile mb-3"
                    >
                        <div className="categories-scroll-container d-flex overflow-auto gap-2 py-2 px-1"
                            style={{ scrollSnapType: 'x mandatory' }}>
                            {isLoading ? (
                                [...Array(5)].map((_, index) => (
                                    <div
                                        key={index}
                                        className="px-3 py-2 rounded-pill shadow-sm"
                                        style={{
                                            backgroundColor: '#e9ecef',
                                            animation: 'pulse 2s infinite',
                                            minWidth: '110px',
                                            height: '38px',
                                            scrollSnapAlign: 'start',
                                            flexShrink: 0
                                        }}
                                    />
                                ))
                            ) : (
                                seccionesMostrar.map((seccion, index) => {
                                    const hasNewCategories = sectionHasNewCategories(seccion);
                                    const isActive = soloSeccion || expanded === `panel${index}`;

                                    return (
                                        <div
                                            key={index}
                                            onClick={() => handleAccordionChange(`panel${index}`)}
                                            className={`px-3 py-2 rounded-pill text-nowrap shadow-sm d-flex align-items-center gap-2 ${isActive ? 'bg-danger text-white' : 'bg-white border border-light'
                                                }`}
                                            style={{
                                                cursor: 'pointer',
                                                scrollSnapAlign: 'start',
                                                flexShrink: 0,
                                                fontSize: '13px',
                                                fontWeight: isActive ? '600' : '500',
                                                transition: 'all 0.2s ease'
                                            }}
                                            title={seccion.nomSeccion}
                                        >
                                            <span>{seccion.nomSeccion}</span>
                                            {hasNewCategories && (
                                                <Badge bg="warning" className="ms-1" style={{ fontSize: '9px' }}>
                                                    Nuevo
                                                </Badge>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}

                {esVistaSeccion && seccionActual && (
                    <div className="mb-4 text-center">
                        <h2 className='fw-bold text-uppercase mb-3' style={{ fontSize: '1.75rem', letterSpacing: '0.5px', color: '#2b235e' }}>
                            {seccionActual.nomSeccion}
                        </h2>
                        <hr
                            style={{
                                width: "120px",
                                margin: "15px auto",
                                borderTop: "3px solid #dc3545",
                                opacity: 1
                            }}
                        />
                    </div>
                )}

                <Row className="g-3 g-md-4">
                    {/* 🖥️ SIDEBAR DESKTOP */}
                    {!esVistaSeccion && (
                        <Col xs={12} md={3} className="d-none d-md-block">
                            <div
                                className="bg-light rounded p-3 shadow-sm"
                                style={{
                                    position: 'sticky',
                                    top: '80px',
                                    maxHeight: 'calc(100vh - 100px)',
                                    overflowY: 'auto',
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#dc3545 transparent'
                                }}
                            >
                                {isLoading ? (
                                    [...Array(6)].map((_, index) => (
                                        <div
                                            key={index}
                                            className="d-flex justify-content-between align-items-center px-3 py-3 mb-2 rounded"
                                            style={{
                                                backgroundColor: '#e9ecef',
                                                animation: 'pulse 2s infinite',
                                                height: '48px'
                                            }}
                                        />
                                    ))
                                ) : (
                                    seccionesMostrar.map((seccion, index) => {
                                        const hasNewCategories = sectionHasNewCategories(seccion);
                                        const isActive = soloSeccion || expanded === `panel${index}`;

                                        return (
                                            <div
                                                key={index}
                                                onClick={() => handleAccordionChange(`panel${index}`)}
                                                className={`d-flex justify-content-between align-items-center px-3 py-3 mb-2 rounded cursor-pointer ${isActive ? 'bg-danger-subtle text-danger fw-semibold' : 'text-dark'
                                                    }`}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderLeft: isActive ? '4px solid #dc3545' : '4px solid transparent',
                                                    transition: 'all 0.2s ease',
                                                    backgroundColor: isActive ? 'rgba(220, 53, 69, 0.1)' : 'transparent'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isActive) {
                                                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.03)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isActive) {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }
                                                }}
                                            >
                                                <span>{seccion.nomSeccion}</span>
                                                <div className="d-flex align-items-center gap-2">
                                                    {hasNewCategories && (
                                                        <Badge bg="danger" className="ms-1">Nuevo</Badge>
                                                    )}
                                                    <ChevronDown
                                                        size={16}
                                                        style={{
                                                            transform: isActive ? 'rotate(-90deg)' : 'rotate(0deg)',
                                                            transition: 'transform 0.2s ease'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </Col>
                    )}

                    {/* 🧱 CATEGORÍAS - RESPONSIVE */}
                    <Col
                        xs={12}
                        md={esVistaSeccion ? 12 : 9}
                    >
                        {isLoading ? (
                            <Row className="g-3 g-md-4">
                                {[...Array(12)].map((_, index) => (
                                    <Col key={index} xs={6} sm={4} lg={3}>
                                        <SkeletonCard />
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                            seccionesMostrar.map((seccion, index) => {
                                if (!esVistaSeccion && expanded !== `panel${index}`) {
                                    return null;
                                }

                                return (
                                    <Row key={index} className="g-3 g-md-4">
                                        {seccion.categorias && seccion.categorias.length > 0 ? (
                                            seccion.categorias.map((categoria) => {
                                                const lastUpdated = new Date(categoria.lastUpdated);
                                                const threeDaysAgo = new Date();
                                                threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
                                                const isNew = lastUpdated >= threeDaysAgo;

                                                return (
                                                    <Col key={categoria.idCategoria} xs={6} sm={4} lg={3}>
                                                        <Link
                                                            to={`/${idSucursal}/Productos/${createSlug(categoria.nomCategoria)}`}
                                                            className="text-decoration-none"
                                                            onClick={() => handleCategoryClick(`panel${index}`)}
                                                        >
                                                            <Card
                                                                className="position-relative border-0 h-100 shadow-sm category-card"
                                                                style={{
                                                                    borderRadius: '16px',
                                                                    overflow: 'hidden',
                                                                    aspectRatio: '1'
                                                                }}
                                                            >
                                                                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                                    <img
                                                                        src={categoria.imgCategoria}
                                                                        alt={categoria.nomCategoria}
                                                                        className="w-100 h-100"
                                                                        loading="lazy"
                                                                        style={{
                                                                            objectFit: 'cover',
                                                                            objectPosition: 'center'
                                                                        }}
                                                                        onError={(e) => {
                                                                            e.target.onerror = null;
                                                                            e.target.src = 'https://via.placeholder.com/300x300?text=Categoria';
                                                                        }}
                                                                    />

                                                                    {/* OVERLAY TOP */}
                                                                    <div
                                                                        className="position-absolute top-0 w-100 text-center text-light fw-semibold px-2 py-2"
                                                                        style={{
                                                                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0.1))',
                                                                            fontSize: '12px',
                                                                            lineHeight: '1.3',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            minHeight: '40px'
                                                                        }}
                                                                    >
                                                                        <div>
                                                                            {categoria.nomCategoria.toUpperCase()}
                                                                            {isNew && (
                                                                                <Badge bg="danger" className="ms-1" style={{ fontSize: '9px' }}>
                                                                                    Nuevo
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {/* OVERLAY BOTTOM */}
                                                                    <div
                                                                        className="position-absolute bottom-0 w-100 text-center text-light fw-semibold"
                                                                        style={{
                                                                            background: 'rgba(0,0,0,0.6)',
                                                                            fontSize: '13px',
                                                                            padding: '8px'
                                                                        }}
                                                                    >
                                                                        {categoria.numProductos} artículos
                                                                    </div>
                                                                </div>
                                                            </Card>
                                                        </Link>
                                                    </Col>
                                                );
                                            })
                                        ) : (
                                            <Col xs={12} className="text-center py-4">
                                                <p className="text-muted">No hay categorías disponibles</p>
                                            </Col>
                                        )}
                                    </Row>
                                );
                            })
                        )}
                    </Col>
                </Row>
            </Container>

            {/* ✅ Botón Flotante Scroll to Top */}
            {showScrollTop && (
                <Button
                    onClick={scrollToTop}
                    className="position-fixed"
                    style={{
                        bottom: '30px',
                        right: '20px',
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        backgroundColor: '#2b235e',
                        borderColor: '#2b235e',
                        boxShadow: '0 4px 12px rgba(43, 35, 94, 0.4)',
                        animation: 'slideUp 0.3s ease-out',
                        transition: 'all 0.2s ease',
                        border: 'none',
                        cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#dc3545';
                        e.currentTarget.style.borderColor = '#dc3545';
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 53, 69, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#2b235e';
                        e.currentTarget.style.borderColor = '#2b235e';
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(43, 35, 94, 0.4)';
                    }}
                    title="Volver al inicio"
                >
                    <ArrowUp size={24} className="text-white" />
                </Button>
            )}
        </div>
    );
}

export default CategoriasUni;
